import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LiveMap from '../components/Map/LiveMap';
import FleetMap from '../components/Map/FleetMap';
import api from '../api/client';
import './Dashboard.css';

interface Device {
  id: string;
  name: string;
  imei: string;
  status: string;
  last_seen: string | null;
  plate_number?: string | null;
  preferred_unit?: string;
}

export default function Dashboard() {
  const { t } = useTranslation('map');
  const [devices, setDevices] = useState<Device[]>([]);
  const [selected, setSelected] = useState<Device | null>(null);
  const [selectedFleetId, setSelectedFleetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/devices/')
      .then((res) => {
        setDevices(res.data);
        if (res.data.length === 1) setSelected(res.data[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="dashboard-loading"><div className="spinner" /></div>;
  }

  if (!devices.length) {
    return (
      <div className="dashboard-empty">
        <p>{t('no_devices')}</p>
        <p className="hint">{t('activate_device')}</p>
      </div>
    );
  }

  // Single device — unchanged simple flow, no selector/fleet map needed.
  if (devices.length === 1) {
    return (
      <div className="dashboard">
        <div className="map-area">
          {selected && <LiveMap deviceId={selected.id} deviceName={selected.name} devicePlate={selected.plate_number} preferredUnit={selected.preferred_unit} />}
        </div>
        {selected && (
          <div className="device-info">
            <div className="device-info-row">
              <span className="device-name">{selected.name}</span>
              <span className={`device-status ${selected.status}`}>{selected.status}</span>
            </div>
            {selected.last_seen && (
              <span className="device-lastseen">
                {t('last_update', { time: new Date(selected.last_seen + 'Z').toLocaleString() })}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Multiple devices — fleet view: all markers on the map, details for the
  // one selected from the list.
  const selectedDevice = devices.find((d) => d.id === selectedFleetId) || null;

  return (
    <div className="dashboard">
      <div className="device-selector">
        {devices.map((d) => (
          <button
            key={d.id}
            className={selectedFleetId === d.id ? 'active' : ''}
            onClick={() => setSelectedFleetId(d.id)}
          >
            {d.name}
          </button>
        ))}
      </div>

      <div className="map-area">
        <FleetMap devices={devices} selectedId={selectedFleetId} onSelect={setSelectedFleetId} />
      </div>

      {selectedDevice && (
        <div className="device-info">
          <div className="device-info-row">
            <span className="device-name">{selectedDevice.name}</span>
            <span className={`device-status ${selectedDevice.status}`}>{selectedDevice.status}</span>
          </div>
          {selectedDevice.last_seen && (
            <span className="device-lastseen">
              {t('last_update', { time: new Date(selectedDevice.last_seen + 'Z').toLocaleString() })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
